import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { DealData } from "@/pages/deals/types";
import { PeopleBadge } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import {
  useDealsQuestionnairesResponses,
  useDealsQuestionnairesSettings
} from "@/pages/deals/hooks";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import Person from "@/components/layouts/QuickView/PeopleSection/Person";
import { usePeople, usePeopleInfinite } from "@/entities/people/hooks";
import { CompanyData } from "@/pages/companies/types";
import { DrawerStateItem } from "@/services/hooks/entities/useQuickViewDrawer";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { PersonData } from "@/entities/people/types";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { find, isEmpty, map } from "lodash";
import { getStagesUpToCurrent } from "@/pages/deals/components/DealStages/utils";

const PEOPLE_PAGE_COUNT = 5;

interface Props {
  deal?: DealData;
  company?: CompanyData;
}

const PeopleSection: React.FC<Props> = ({ deal, company }) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(PEOPLE_PAGE_COUNT);

  useEffect(() => {
    setVisibleCount(PEOPLE_PAGE_COUNT);
  }, [deal, company]);

  const stages = useDealStages().data?.stages;

  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const handleOpenDrawer = useCallback(
    (item: DrawerStateItem) => {
      if (!item) return;
      newOpenDrawer(item.id, EntityType.PERSON);
    },
    [newOpenDrawer]
  );
  const peopleFilter = useMemo(() => {
    return { companyId: company?.id ?? "" };
  }, [company?.id]);

  const { entities: companyPeople, isLoading: isLoadingPeople } =
    usePeopleInfinite(
      peopleFilter,
      undefined,
      undefined,
      undefined,
      "firstName",
      true,
      true,
      Boolean(company)
    );

  const allDealPeopleIds = deal?.allDealPeople ?? [];

  const { entities: allDealPeople } = usePeople(allDealPeopleIds, {
    enabled: Boolean(deal) && allDealPeopleIds.length > 0
  });

  const people = company
    ? [...companyPeople]
    : ([...allDealPeople] as PersonData[]);

  const currentStage = useMemo(
    () => find(stages, (stage) => stage.id === deal?.stage),
    [stages, deal?.stage]
  );

  const stagesUpToCurrent = useMemo(
    () => getStagesUpToCurrent(currentStage, stages),
    [currentStage, stages]
  );

  const { data: questionnairesSettings } =
    useDealsQuestionnairesSettings(stagesUpToCurrent);

  const mappingQuestionnaire = useMemo(() => {
    if (!questionnairesSettings) return undefined;

    for (const stageSettings of Object.values(questionnairesSettings)) {
      const found = stageSettings?.data?.questionnaires?.find(
        (q) => q.typeValue === QuestionnaireType.MAPPING
      );
      if (found) return found;
    }
    return undefined;
  }, [questionnairesSettings]);

  const { data: questionnairesResponses } = useDealsQuestionnairesResponses(
    deal?.id || "",
    [mappingQuestionnaire?.id || ""]
  );

  const mappedPeople = useMemo(() => {
    const questions = mappingQuestionnaire?.questions;
    const responses =
      questionnairesResponses[mappingQuestionnaire?.id || ""]?.data?.responses;

    if (!questions || !responses) return {};

    const personRoleMap: { [personId: string]: string[] } = {};

    questions.forEach((question) => {
      const responseValue = responses[question?.id]?.response?.value;
      if (responseValue) {
        const personId = responseValue as string;
        if (personRoleMap[personId]) {
          personRoleMap[personId].push(question.questionText);
        } else {
          personRoleMap[personId] = [question.questionText];
        }
      }
    });

    return personRoleMap;
  }, [questionnairesResponses, mappingQuestionnaire]);

  const sortedPeople = useMemo(() => {
    if (!people || people.length === 0) return [];

    const mappedPersonIds = Object.keys(mappedPeople);
    const mappedPersons = people.filter((person) =>
      mappedPersonIds.includes(person.id)
    );
    const unmappedPersons = people.filter(
      (person) => !mappedPersonIds.includes(person.id)
    );

    return [...mappedPersons, ...unmappedPersons];
  }, [people, mappedPeople]);

  const shouldShowMore = sortedPeople.length > visibleCount;
  const visiblePeople = sortedPeople.slice(0, visibleCount);

  const handleShowMore = () => {
    setVisibleCount((prev) =>
      Math.min(prev + PEOPLE_PAGE_COUNT, sortedPeople.length)
    );
  };

  return (
    <>
      <div className="flex flex-col gap-4 border-[0.5px] border-border-border1 bg-surface-elevation2 w-full p-4 rounded-xl">
        <Text variant="lgLabel">{t("people.title")}</Text>
        {isLoadingPeople ? (
          <div className="flex w-full h-full items-center justify-center">
            <Spinner size="5" />
          </div>
        ) : isEmpty(visiblePeople) ? (
          <div className="p-2 flex flex-col items-center">
            <IconContainer className="size-14">
              <PeopleBadge />
            </IconContainer>
            <Text variant="bodyTertiary">
              {t("quickView.people.empty", {
                entity: t(
                  `entities.names.${deal ? "deal" : "company"}`
                ).toLowerCase()
              })}
            </Text>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="flex flex-col gap-2 transition-all duration-300 ease-in-out">
              {map(visiblePeople, (person) => (
                <Person
                  key={person?.id}
                  person={person}
                  mapping={mappedPeople?.[person?.id]}
                  openDrawer={handleOpenDrawer}
                />
              ))}
            </div>
            {shouldShowMore && (
              <Button
                variant="tertiary"
                className="w-fit h-fit self-end mt-2"
                onClick={handleShowMore}
              >
                {t("showNumMore", {
                  num:
                    sortedPeople.length - visibleCount > 5
                      ? 5
                      : sortedPeople.length - visibleCount
                })}
              </Button>
            )}
          </div>
        )}
      </div>
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={EntityType.PERSON}
        onClose={newCloseDrawer}
      />
    </>
  );
};

export default PeopleSection;
